import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Loader2, Star, Crown, Check, CreditCard, Lock } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import RegistrationWithPayment from './RegistrationWithPayment';

// Load Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51QT9MKRXVB5cI8aLxe1sxS7rZWYCQFuX8sPQJCKtxmEh2xRJe3dJNkBWDqIvPHqSqMEpPSgLNGDvPjXaRJqNRAif003J47Qcts');

const AuthModal = ({ children, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { toast } = useToast();

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  const [registerForm, setRegisterForm] = useState({
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    selectedPlan: 'seekers' // Default to Seekers plan
  });

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(loginForm);
      
      if (result.success) {
        toast({
          title: "Welcome back!",
          description: "You have successfully logged in.",
        });
        setOpen(false);
        onSuccess?.(result.user);
        
        // Reset form
        setLoginForm({ email: '', password: '' });
      } else {
        toast({
          variant: "destructive",
          title: "Login Failed",
          description: result.error,
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: "An unexpected error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Password Mismatch",
        description: "Passwords do not match.",
      });
      return;
    }

    if (registerForm.password.length < 6) {
      toast({
        variant: "destructive",
        title: "Invalid Password",
        description: "Password must be at least 6 characters long.",
      });
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, selectedPlan, ...userData } = registerForm;
      const result = await register(userData);
      
      if (result.success) {
        toast({
          title: "Welcome to TribalRoot.AI!",
          description: "Redirecting you to complete your subscription...",
        });
        
        // Store selected plan in sessionStorage for checkout page
        sessionStorage.setItem('selected_plan', selectedPlan);
        
        // Redirect to subscription page to complete payment
        setTimeout(() => {
          setOpen(false);
          window.location.href = '/subscription';
        }, 1500);
      } else {
        toast({
          variant: "destructive",
          title: "Registration Failed",
          description: result.error,
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Registration Failed",
        description: "An unexpected error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Login to TribalRoot.AI</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleLoginSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              type="email"
              value={loginForm.email}
              onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
              required
              placeholder="Enter your email"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="login-password">Password</Label>
            <Input
              id="login-password"
              type="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
              required
              placeholder="Enter your password"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <a 
              href="/forgot-password" 
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                window.location.href = '/forgot-password';
              }}
            >
              Lost Password?
            </a>
            <a 
              href="/register" 
              className="text-sm text-green-600 hover:text-green-800 hover:underline"
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                window.location.href = '/register';
              }}
            >
              Need an account?
            </a>
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-green-700 hover:bg-green-800" 
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;